import { zodResolver } from "@hookform/resolvers/zod";
import {
    Avatar,
    Box,
    Button,
    Card,
    Checkbox,
    Col,
    Container,
    Grid,
    Group,
    Radio,
    Stack,
    Text,
    TextInput,
    Title
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { showNotification } from "@mantine/notifications";
import { Building2, GraduationCap, Heart, Megaphone, Network, Plus, Rocket, ShoppingCart, Store, TrendingUp, User, Video } from "lucide-react";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import * as z from "zod";
import InputField, { InputFieldTypes } from "../components/input_field";
import { useAuth } from "../contexts/auth.context";
import { useNoCaptcha } from "../hooks/useNoCaptcha";
import { api } from "../services/api";
import { RECEIVE_OTP } from "../services/auth";
import { isValidCpfCnpj } from "../utility/document";
import { phoneCountryCodes, useQuery } from "../utility/util";

const step1Schema = z.object({
    nome: z.string().trim().min(1, "Nome é obrigatório").max(100, "Nome muito longo"),
    email: z.string().email("Email inválido").max(255, "Email muito longo"),
    phone_country_code: z.string().trim().min(1, "Código do país é obrigatório").max(5, "Código inválido"),
    phone: z.string().trim().min(6, "Telefone inválido").max(20, "Telefone inválido"),
    password: z.string().min(8, "Senha deve ter pelo menos 8 caracteres"),
    confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
    message: "Senhas não coincidem",
    path: ["confirmPassword"],
});

const step2Schema = z.object({
    nomeEmpresa: z.string().trim().min(1, "Nome da empresa é obrigatório").max(100, "Nome muito longo"),
    emailEmpresa: z.string().email("Email inválido").max(255, "Email muito longo"),
    registerNumber: z.string().trim().refine((value) => isValidCpfCnpj(value), "CPF/CNPJ inválido"),
    phone_country_code: z.string().trim().min(1, "Código do país é obrigatório").max(5, "Código inválido"),
    phone: z.string().trim().min(6, "Telefone inválido").max(20, "Telefone inválido"),
});

const step3Schema = z.object({
    tipoEmpresa: z.string().min(1, "Selecione o tipo de empresa"),
});

const step4Schema = z.object({
    quantidadeClientes: z.string().min(1, "Selecione a quantidade de clientes"),
    objetivos: z.array(z.string()).min(1, "Selecione pelo menos um objetivo"),
});

type Step1Data = z.infer<typeof step1Schema>;
type Step2Data = z.infer<typeof step2Schema>;
type Step3Data = z.infer<typeof step3Schema>;
type Step4Data = z.infer<typeof step4Schema>;

const companyTypes = [
    {
        id: "agencia-marketing",
        label: "Agência de Marketing / Social Media",
        description: "Agência full-service, conteúdo e gestão de redes",
        icon: Megaphone
    },
    {
        id: "agencia-lancamento",
        label: "Agência de Lançamentos / Co-Produtor",
        description: "Especializado em lançamentos, perpétuo ou funis",
        icon: Rocket
    },
    {
        id: "freelancer",
        label: "Social Media Freelancer",
        description: "Profissional autônomo ou microoperação",
        icon: User
    },
    {
        id: "ecommerce",
        label: "E-commerce / Loja Virtual",
        description: "Loja online, marketplace, dropshipping",
        icon: ShoppingCart
    },
    {
        id: "pequena-empresa",
        label: "Pequena Empresa / Negócio Local",
        description: "Restaurante, clínica, loja física, escritório",
        icon: Store
    },
    {
        id: "criador-conteudo",
        label: "Criador de Conteúdo / Influenciador",
        description: "Influencers, criadores de vídeo, podcasts",
        icon: Video
    },
    {
        id: "trafego",
        label: "Gestor de Tráfego",
        description: "Profissional ou agência de mídia paga",
        icon: TrendingUp
    },
    {
        id: "ong",
        label: "ONG / Projeto Social",
        description: "Instituições sem fins lucrativos",
        icon: Heart
    },
    {
        id: "franqueadora",
        label: "Franqueadora",
        description: "Matriz que precisa padronizar conteúdos",
        icon: Network
    },
    {
        id: "franqueada",
        label: "Empresa Franqueada",
        description: "Unidade que segue diretrizes da franqueadora",
        icon: Building2
    },
    {
        id: "educadores",
        label: "Educadores / Infoprodutores",
        description: "Mentores, consultores, experts",
        icon: GraduationCap
    },
    {
        id: "outra",
        label: "Outra",
        description: "Qualquer segmento fora da lista",
        icon: Plus
    },
];

const objetivos = [
    "Agendar e automatizar postagens",
    "Organizar conteúdos e aprovações",
    "Reduzir retrabalho e ganhar produtividade",
    "Escalar a operação da agência",
    "Ainda não sei",
];

export default function Signup({ customer = false }) {
    const [currentStep, setCurrentStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [step1Data, setStep1Data] = useState<Step1Data | null>(null);
    const [step2Data, setStep2Data] = useState<Step2Data | null>(null);
    const [step3Data, setStep3Data] = useState<Step3Data | null>(null);
    const [otp, setOtp] = useState<string>("");
    const [otpRegenerateTimeout, setOtpRegenerateTimeout] = useState<number>(0);
    const isMobile = useMediaQuery('(max-width: 987px)');

    const { user, signIn, ip } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state as { from: Location };

    const query = useQuery();
    const queryFrom = query.get("from");
    const queryMail = query.get("email");
    const utm_source = query.get("utm_source");
    const utm_medium = query.get("utm_medium");
    const utm_campaign = query.get("utm_campaign");
    const utm_term = query.get("utm_term");
    const gclid = query.get("gclid");
    const fbclid = query.get("fbclid");

    const from = (queryFrom !== "/" ? queryFrom : null) ||
        (state?.from?.pathname !== "/" ? state?.from?.pathname : null) || 
        (customer ? "/" : "/bem-vindo");

    const { getNoCaptcha } = useNoCaptcha();

    const step1Form = useForm<Step1Data>({
        resolver: zodResolver(step1Schema),
        defaultValues: {
            nome: "",
            email: "",
            phone_country_code: "55",
            phone: "",
            password: "",
            confirmPassword: "",
        },
    });

    const step2Form = useForm<Step2Data>({
        resolver: zodResolver(step2Schema),
        defaultValues: {
            nomeEmpresa: "",
            emailEmpresa: "",
            phone_country_code: "55",
            phone: "",
            registerNumber: "",
        },
    });

    const step3Form = useForm<Step3Data>({
        resolver: zodResolver(step3Schema),
        defaultValues: {
            tipoEmpresa: "",
        },
    });

    const step4Form = useForm<Step4Data>({
        resolver: zodResolver(step4Schema),
        defaultValues: {
            quantidadeClientes: "",
            objetivos: [],
        },
    });

    const onStep1Submit = (data: Step1Data) => {
        setStep1Data(data);
        step2Form.setValue('phone', data.phone)
        step2Form.setValue('phone_country_code', data.phone_country_code)
        step2Form.setValue('emailEmpresa', data.email)
        // for customers, next step is OTP (2). For companies, go to tipoEmpresa (2).
        setCurrentStep(2);
    };

    const onStep2Submit = (data: Step2Data) => {
        setStep2Data(data);
        setCurrentStep(3);
    };

    const onStep3Submit = (data: Step3Data) => {
        setStep3Data(data);
        setCurrentStep(4);
    };

    const onStep4Submit = async (data: Step4Data) => {
        if (!loading) {
            setLoading(true);
            const captchaToken = await getNoCaptcha("signup");

            // montar payload no formato solicitado
            const general = {
                name: step1Data?.nome ?? "",
                register_number: step2Data.registerNumber,
                phone_country_code: step1Data?.phone_country_code ?? "",
                phone: (step1Data?.phone ?? "").replace(/ |\-|\(|\)|\_/g, ""),
                email: step1Data?.email ?? "",
                password: step1Data?.password ?? "",
                password_confirmation: step1Data?.confirmPassword ?? "",
            };

            const company = customer ? undefined : {
                type: step3Data?.tipoEmpresa ?? "",
                customers_qt: data.quantidadeClientes,
                goals: data.objetivos,
                name: step2Data?.nomeEmpresa ?? "",
                register_number: step2Data.registerNumber,
                phone: (step2Data?.phone ?? "").replace(/ |\-|\(|\)|\_/g, ""),
                phone_country_code: step2Data?.phone_country_code ?? "",
                email: step2Data?.emailEmpresa ?? "",
            };

            const finalData = {
                company,
                general,
                others: {
                    captchaToken,
                    otp,
                    utm_campaign,
                    utm_source,
                    utm_medium,
                    utm_term,
                    gclid,
                    fbclid,
                }
            };

            api.post(customer ? `/auth/signup` : `/auth/signup/company`, finalData)
                .then(({ data: { user, insertedCompany } }) => {
                    try {
                        (window as any).fbq(
                            'track',
                            (finalData?.company?.goals ?? []).includes("Ainda não sei") ? 'Trial_Low_Quality' : 'StartTrial',
                            {
                                ip,
                                name: finalData?.general?.name,
                                email: finalData?.general?.email,
                                phone: `+${finalData?.general?.phone_country_code}${finalData?.general?.phone.replace(/\(|\)|\-|\.| /g, "")}`,
                            },
                            {
                                eventID: insertedCompany?._id,
                            }
                        );
                    } catch (ignored) { }
                    try {
                        (window as any).ttq.track('StartTrial', {
                            content_type: "subscription",
                            name: finalData?.general?.name,
                            email: finalData?.general?.email,
                            phone: `+${finalData?.general?.phone_country_code}${finalData?.general?.phone.replace(/\(|\)|\-|\.| /g, "")}`,
                        });
                    } catch (ignored) { }
                    try {
                        (window as any).gtag('event', 'start_trial', {
                            event_name: "start_trial",
                            name: finalData?.general?.name,
                            email: finalData?.general?.email,
                            phone: `+${finalData?.general?.phone_country_code}${finalData?.general?.phone.replace(/\(|\)|\-|\.| /g, "")}`,
                        })
                    } catch (ignored) { }

                    setLoading(false);
                    signIn(user, () => navigate(from, { replace: true }));
                })
                .catch(err => {
                    setLoading(false);
                    showNotification({ title: "Ops", message: err.message, color: 'red' })
                })
        }
    }

    // Generate OTP for customer flow
    const generateOtp = () => {
        const phone = (step1Data?.phone ?? "").replace(/ |\-|\(|\)|\_/g, "");
        const country_code = (step1Data?.phone_country_code ?? "55").replace(/\+/g, "");
        if (!phone) {
            showNotification({ title: "Ops", message: "Número inválido para envio do código", color: "red" });
            return;
        }

        RECEIVE_OTP({
            country_code,
            phone,
        }).then(() => {
            // silent
        }).catch(err => {
            showNotification({ title: "Ops", message: err.message, color: "red" });
        });
    }

    useEffect(() => {
        if (customer && currentStep === 2 && step1Data) {
            generateOtp();
            const resendAt = moment().add(2, "minutes");
            const otpInterval = setInterval(() => {
                const diff = moment(resendAt).diff(moment(), "seconds");
                setOtpRegenerateTimeout(diff > 0 ? diff : 0);
            }, 200);
            return () => clearInterval(otpInterval);
        }
    }, [customer, currentStep, step1Data]);

    useEffect(() => {
        step1Form.setValue('email', queryMail)
    }, [queryMail])

    return (
        <Container fluid style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, background: '#FAFAFA' }}>
            <Grid style={{ maxWidth: 1050, width: "100%" }} gutter="xl">
                {!isMobile && <Col span={12} md={3}>
                    <Stack spacing="md">
                        <Box>
                            <Title order={1} c="blue" fw="bold">aprova aí.</Title>
                        </Box>

                        <Stack spacing="sm" pt={24}>
                            {customer ? (
                                <>
                                    <Group align="flex-start" spacing="sm">
                                        <Avatar radius="md" variant={currentStep >= 1 ? "filled" : "light"} color={currentStep >= 1 ? "blue" : "gray"}>{1}</Avatar>
                                        <Box>
                                            <Text weight={600}>Dados pessoais</Text>
                                            <Text size="sm" color="dimmed">Informações básicas</Text>
                                        </Box>
                                    </Group>

                                    <Group align="flex-start" spacing="sm">
                                        <Avatar radius="md" variant={currentStep >= 2 ? "filled" : "light"} color={currentStep >= 2 ? "blue" : "gray"}>{2}</Avatar>
                                        <Box>
                                            <Text weight={600}>Verificação</Text>
                                            <Text size="sm" color="dimmed">Confirme seu Whatsapp</Text>
                                        </Box>
                                    </Group>
                                </>
                            ) : (
                                <>
                                    <Group align="flex-start" spacing="sm">
                                        <Avatar radius="md" variant={currentStep >= 1 ? "filled" : "light"} color={currentStep >= 1 ? "blue" : "gray"}>{1}</Avatar>
                                        <Box>
                                            <Text weight={600}>Dados pessoais</Text>
                                            <Text size="sm" color="dimmed">Informações básicas</Text>
                                        </Box>
                                    </Group>

                                    <Group align="flex-start" spacing="sm">
                                        <Avatar radius="md" variant={currentStep >= 2 ? "filled" : "light"} color={currentStep >= 2 ? "blue" : "gray"}>{2}</Avatar>
                                        <Box>
                                            <Text weight={600}>Dados da marca</Text>
                                            <Text size="sm" color="dimmed">Informações da empresa</Text>
                                        </Box>
                                    </Group>

                                    <Group align="flex-start" spacing="sm">
                                        <Avatar radius="md" variant={currentStep >= 3 ? "filled" : "light"} color={currentStep >= 3 ? "blue" : "gray"}>{3}</Avatar>
                                        <Box>
                                            <Text weight={600}>Tipo de empresa</Text>
                                            <Text size="sm" color="dimmed">Qual seu segmento</Text>
                                        </Box>
                                    </Group>

                                    <Group align="flex-start" spacing="sm">
                                        <Avatar radius="md" variant={currentStep >= 4 ? "filled" : "light"} color={currentStep >= 4 ? "blue" : "gray"}>{4}</Avatar>
                                        <Box>
                                            <Text weight={600}>Seus objetivos</Text>
                                            <Text size="sm" color="dimmed">O que espera alcançar</Text>
                                        </Box>
                                    </Group>
                                </>
                            )}
                        </Stack>
                    </Stack>
                </Col>}

                <Col span={12} md={9}>
                    <Card p="38px" withBorder>
                        {currentStep === 1 && (
                            <Stack spacing="lg">
                                <Box>
                                    <Title order={2}>Vamos começar!</Title>
                                    <Text color="dimmed">Preencha seus dados para continuar.</Text>
                                </Box>

                                <form onSubmit={step1Form.handleSubmit(onStep1Submit)}>
                                    <Stack spacing="md">
                                        <TextInput
                                            id="nome"
                                            label="Nome"
                                            placeholder="Seu nome completo"
                                            {...step1Form.register("nome")}
                                            error={step1Form.formState.errors.nome?.message as any}
                                        />

                                        <TextInput
                                            id="email"
                                            label="Email"
                                            type="email"
                                            placeholder="seu@email.com"
                                            {...step1Form.register("email")}
                                            error={step1Form.formState.errors.email?.message as any}
                                        />

                                        <Group grow>
                                            <InputField
                                                title="País"
                                                mb="0"
                                                name="phone_country_code"
                                                autoComplete="tel-country-code"
                                                fieldType={InputFieldTypes.SELECT}
                                                value={step1Form.watch("phone_country_code")}
                                                onChange={({ vl }) => step1Form.setValue("phone_country_code", vl)}
                                                nothingFound="No country code found"
                                                options={phoneCountryCodes}
                                            />
                                            <TextInput
                                                id="phone"
                                                label="WhatsApp"
                                                placeholder="(00) 00000-0000"
                                                {...step1Form.register("phone")}
                                                error={step1Form.formState.errors.phone?.message as any}
                                            />
                                        </Group>

                                        <TextInput
                                            id="password"
                                            label="Senha"
                                            type="password"
                                            placeholder="Mínimo 8 caracteres"
                                            {...step1Form.register("password")}
                                            error={step1Form.formState.errors.password?.message as any}
                                        />

                                        <TextInput
                                            id="confirmPassword"
                                            label="Confirmar Senha"
                                            type="password"
                                            placeholder="Confirme sua senha"
                                            {...step1Form.register("confirmPassword")}
                                            error={step1Form.formState.errors.confirmPassword?.message as any}
                                        />

                                        <Button type="submit" fullWidth>
                                            Continuar
                                        </Button>
                                    </Stack>
                                </form>
                            </Stack>
                        )}

                        {currentStep === 2 && !customer && (
                            <Stack spacing="lg">
                                <Box>
                                    <Title order={2}>Dados da sua marca</Title>
                                    <Text color="dimmed">Preencha as informações da sua empresa.</Text>
                                </Box>

                                <form onSubmit={step2Form.handleSubmit(onStep2Submit)}>
                                    <Stack spacing="md">
                                        <TextInput
                                            id="nomeEmpresa"
                                            label="Nome da Empresa"
                                            placeholder="Nome da sua empresa"
                                            {...step2Form.register("nomeEmpresa")}
                                            error={step2Form.formState.errors.nomeEmpresa?.message as any}
                                        />

                                        <TextInput
                                            id="emailEmpresa"
                                            label="Email da Empresa"
                                            type="email"
                                            placeholder="contato@empresa.com"
                                            {...step2Form.register("emailEmpresa")}
                                            error={step2Form.formState.errors.emailEmpresa?.message as any}
                                        />

                                        <Group grow>
                                            <InputField
                                                title="País"
                                                mb="0"
                                                name="phone_country_code"
                                                autoComplete="tel-country-code"
                                                fieldType={InputFieldTypes.SELECT}
                                                value={step2Form.watch("phone_country_code")}
                                                onChange={({ vl }) => step2Form.setValue("phone_country_code", vl)}
                                                nothingFound="No country code found"
                                                options={phoneCountryCodes}
                                            />
                                            <TextInput
                                                id="telefoneEmpresa"
                                                label="Telefone da Empresa"
                                                placeholder="(00) 0000-0000"
                                                {...step2Form.register("phone")}
                                                error={step2Form.formState.errors.phone?.message as any}
                                            />
                                        </Group>

                                        <TextInput
                                            id="registerNumber"
                                            label="CPF/CNPJ"
                                            {...step2Form.register("registerNumber")}
                                            error={step2Form?.formState.errors.registerNumber?.message as any}
                                        />

                                        <Group grow>
                                            <Button variant="outline" onClick={() => setCurrentStep(1)}>
                                                Voltar
                                            </Button>
                                            <Button type="submit">
                                                Continuar
                                            </Button>
                                        </Group>
                                    </Stack>
                                </form>
                            </Stack>
                        )}

                        {currentStep === 3 && !customer && (
                            <Stack spacing="lg">
                                <Box>
                                    <Title order={2}>Em que tipo de empresa você trabalha?</Title>
                                    <Text color="dimmed">Selecione abaixo o tipo de empresa que mais se aproxima da sua realidade.</Text>
                                </Box>

                                <form onSubmit={step3Form.handleSubmit(onStep3Submit)}>
                                    <Stack spacing="md">
                                        <Grid>
                                            {companyTypes.map((company) => {
                                                const Icon = company.icon;
                                                const isSelected = step3Form.watch("tipoEmpresa") === company.id;

                                                return (
                                                    <Col span={12} md={4} key={company.id}>
                                                        <Card
                                                            withBorder
                                                            radius="md"
                                                            padding="md"
                                                            onClick={() => step3Form.setValue("tipoEmpresa", company.id)}
                                                            style={{
                                                                cursor: "pointer",
                                                                borderColor: isSelected ? undefined : undefined,
                                                                background: isSelected ? "rgba(0, 112, 244, 0.2)" : undefined,
                                                            }}
                                                        >
                                                            <Stack align="center" spacing="xs">
                                                                <Avatar radius="xl" color={isSelected ? "blue" : "gray"}>
                                                                    <Icon size={20} />
                                                                </Avatar>
                                                                <Box style={{ textAlign: "center" }}>
                                                                    <Text weight={600} size="sm">{company.label}</Text>
                                                                    <Text size="xs" color="dimmed">{company.description}</Text>
                                                                </Box>
                                                            </Stack>
                                                        </Card>
                                                    </Col>
                                                );
                                            })}
                                        </Grid>

                                        {step3Form.formState.errors.tipoEmpresa && (
                                            <Text size="sm" color="red">{step3Form.formState.errors.tipoEmpresa.message}</Text>
                                        )}

                                        <Group grow>
                                            <Button variant="outline" onClick={() => setCurrentStep(2)}>
                                                Voltar
                                            </Button>
                                            <Button type="submit">
                                                Continuar
                                            </Button>
                                        </Group>
                                    </Stack>
                                </form>
                            </Stack>
                        )}

                        {currentStep === 4 && !customer && (
                            <Stack spacing="lg">
                                <Box>
                                    <Title order={2}>Vamos conhecer suas necessidades</Title>
                                    <Text color="dimmed">Essas informações nos ajudam a personalizar sua experiência.</Text>
                                </Box>

                                <form onSubmit={step4Form.handleSubmit(onStep4Submit)}>
                                    <Stack spacing="md">
                                        <Box>
                                            <Text weight={500}>Quantos clientes gerencia?</Text>
                                            <Radio.Group
                                                value={step4Form.watch("quantidadeClientes")}
                                                onChange={(value) => step4Form.setValue("quantidadeClientes", value)}
                                            >
                                                <Stack spacing="sm">
                                                    <Group position="left" spacing="sm" style={{ width: "100%" }}>
                                                        <Radio value="ate-3" id="ate-3" label="Até 3 clientes" />
                                                    </Group>
                                                    <Group position="left" spacing="sm" style={{ width: "100%" }}>
                                                        <Radio value="4-10" id="4-10" label="4 a 10 clientes" />
                                                    </Group>
                                                    <Group position="left" spacing="sm" style={{ width: "100%" }}>
                                                        <Radio value="11-20" id="11-20" label="11 a 20 clientes" />
                                                    </Group>
                                                    <Group position="left" spacing="sm" style={{ width: "100%" }}>
                                                        <Radio value="20+" id="20+" label="Mais de 20 clientes" />
                                                    </Group>
                                                </Stack>
                                            </Radio.Group>

                                            {step4Form.formState.errors.quantidadeClientes && (
                                                <Text size="sm" color="red">{step4Form.formState.errors.quantidadeClientes.message}</Text>
                                            )}
                                        </Box>

                                        <Box>
                                            <Text weight={600}>Qual seu objetivo com a plataforma?</Text>
                                            <Text size="sm" color="dimmed">Você pode selecionar mais de uma opção.</Text>

                                            <Stack spacing="sm" mt="xs">
                                                {objetivos.map((objetivo) => {
                                                    const objetivosAtuais = step4Form.watch("objetivos") || [];
                                                    const isChecked = objetivosAtuais.includes(objetivo);

                                                    return (
                                                        <Card key={objetivo} withBorder radius="md" padding="sm" style={{ cursor: 'pointer' }} onClick={() => {
                                                            const current = step4Form.getValues("objetivos") || ([] as string[]);
                                                            if (current.includes(objetivo)) {
                                                                step4Form.setValue("objetivos", current.filter((o: string) => o !== objetivo));
                                                            } else {
                                                                step4Form.setValue("objetivos", [...current, objetivo]);
                                                            }
                                                        }}>
                                                            <Group noWrap align="center">
                                                                <Checkbox id={objetivo} checked={isChecked} />
                                                                <Text>{objetivo}</Text>
                                                            </Group>
                                                        </Card>
                                                    );
                                                })}
                                            </Stack>

                                            {step4Form.formState.errors.objetivos && (
                                                <Text size="sm" color="red">{step4Form.formState.errors.objetivos.message}</Text>
                                            )}
                                        </Box>

                                        <Group grow>
                                            <Button variant="outline" onClick={() => setCurrentStep(3)}>
                                                Voltar
                                            </Button>
                                            <Button
                                                id={currentStep === (customer ? 2 : 4) ? "StartTrial" : "Continue"}

                                                type="submit" loading={loading}>
                                                Finalizar cadastro
                                            </Button>
                                        </Group>
                                    </Stack>
                                </form>
                            </Stack>
                        )}
                    </Card>
                </Col>
            </Grid>
        </Container>
    );
}